import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  Log,
  PLURAL_KEY,
  RydeProcessTitle,
  RydeUiUnit,
  selectedIdsSF,
  TAddressedFieldsValueOnCompleted,
  useProcesses,
} from "@ryde/shared";

import { useDynamicFormsListActions } from "../use-dynamic-forms-list-actions";

export const useWatchBulkNotificationResolve = ({
  entity,
  addressedFieldsValueOnCompleted,
}: {
  entity: RydeUiUnit;
  addressedFieldsValueOnCompleted: TAddressedFieldsValueOnCompleted;
}) => {
  const selectedIds = useRecoilValue(selectedIdsSF(entity));
  const { removeRows } = useDynamicFormsListActions(entity);
  const bulkUpdateNotificationResolvedProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkUpdateNotificationResolved,
    entity,
  );
  const { stopProcessPolling } = useProcesses();
  const bulkUpdateNotificationResolvedProcess = useRecoilValue(
    activeProcessSF(bulkUpdateNotificationResolvedProcessKey),
  );
  const { t } = useTranslation();

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      addressedFieldsValueOnCompleted(process, false);
      removeRows(selectedIds);

      Log.infoAndToast(
        t("general.ENTITY_SUCCESSFULLY_RESOLVED", {
          entity: t(`${entity}.${PLURAL_KEY}`),
        }),
      );
    },
    [addressedFieldsValueOnCompleted, entity, removeRows, selectedIds, t],
  );

  useEffect(() => {
    handleProcess({
      entity,
      process: bulkUpdateNotificationResolvedProcess,
      onComplete,
      translationFunction: t,
      processTitle: RydeProcessTitle.BulkUpdateNotificationResolved,
      stopProcessPolling,
    });
  }, [
    bulkUpdateNotificationResolvedProcess,
    entity,
    onComplete,
    stopProcessPolling,
    t,
  ]);
};
